// Phase 5.2 — Vendors screen, migrated to shadcn/Tailwind.
//
// UI only: the MUI Table, the ApparelSubmenu toolbar, the antd Pagination and the
// antd Modal (activate/deactivate/delete confirm) are replaced by the shared
// DataTable (VendorTable) with built-in search/sort/pagination, a PageHeader and
// a ConfirmDialog. Every dispatch / useSelector / navigate / payload is unchanged
// — the handlers below are the legacy ones (getVendorListAction /
// createOrUpdateVendorAction). Add button navigates to /vendor/create — the
// same fully-wired create screen "Edit" (key "1" below) already navigates to.
import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import ApparelMenu from "./SubComponents/apparelMenu";
import AppConstant from "../Globals/AppConstants";
import { useDispatch, useSelector } from "react-redux";
import { createOrUpdateVendorAction, getVendorListAction, getVendorPurchaseHistoryAction } from "../Store/actions/apparelAction";
import { deepCopyFunction } from "../Utils/helpers";
import { useLocation, useNavigate } from "react-router-dom";
import { ConfirmDialog } from "../shared/confirm-dialog";
import { Button } from "ui/button";
import VendorTable from "./users/VendorTable";
import PurchaseHistorySheet from "./users/PurchaseHistorySheet";

const VendorList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const { getVendorListOnLoad, getVendorListData, createOrUpdateVendorOnLoad, getVendorPurchaseHistoryData } = apparelReducerState;
    const getVendorList = getVendorListData?.getVenderList?.vendorDetails;
    const getVendorListPage = getVendorListData?.getVenderList?.page;
    const vendorPurchaseHistory = getVendorPurchaseHistoryData?.getVendorPurchaseHistory?.rows;
    let vendorTemp = {
        searchText: "",
        sortBy: "0",
        isActive: "",
        limit: 10,
        offset: 0
    }

    let createOrUpdateVendor = {
        vendorId: "",
        name: null,
        description: null,
        emailId: null,
        mobileNumber: null,
        websiteUrl: null,
        contactName: null,
        streetArea: null,
        city: null,
        zipcode: null,
        stateRefId: null,
        isActive: null,
        isDeleted: null
    }
    const [vendorInputs, setVendorInputs] = useState<any>(() => {
        const auditCode = location.state?.auditSearchCode;
        return auditCode ? { ...deepCopyFunction(vendorTemp), searchText: auditCode } : deepCopyFunction(vendorTemp);
    });
    // Arriving from Audit Log: re-run this screen's own search with the
    // record's business code, then navigate to its edit page once the
    // matching row (by vendorId, the uniqueKey alias) shows up in the list —
    // Vendor has a real edit UI, so Audit Log goes straight to it.
    const [pendingAuditRecordId, setPendingAuditRecordId] = useState<string | undefined>(location.state?.auditRecordId);
    const [createOrUpdateVendorInputs, setCreateOrUpdateVendorInputs] = useState<any>(deepCopyFunction(createOrUpdateVendor));
    const [createOrUpdateVendorLoad, setCreateOrUpdateVendorLoad] = useState(false);
    const [vendorDeleteInactiveOpen, setVendorDeleteInactiveOpen] = useState(false);
    const [key, setKey] = useState(null);
    const [purchaseHistoryOpen, setPurchaseHistoryOpen] = useState(false);
    const [vendorHistorySubject, setVendorHistorySubject] = useState<any>(null);

    const getVendorPurchaseHistoryApi = (item: any) => {
        dispatch(getVendorPurchaseHistoryAction({ vendorId: item?.vendorId }));
        setVendorHistorySubject(item);
        setPurchaseHistoryOpen(true);
    }

    const onChangePagination = (page: any) => {
        setVendorInputs({ ...vendorInputs, offset: ((page * 10) - 10 == 0) ? 0 : (page * 10) - 10 })
    }

    const getSearchText = (value: any) => {
        setVendorInputs({ ...vendorInputs, searchText: value, offset: 0 })
    }

    const getOrderData = (value: any) => {
        setVendorInputs({ ...vendorInputs, sortBy: value, offset: 0 })
    }

    const getStatusFilter = (value: any) => {
        setVendorInputs({ ...vendorInputs, isActive: value, offset: 0 })
    }

    const getVendorListApi = () => {
        let payload = {
            searchText: vendorInputs?.searchText,
            sortBy: vendorInputs?.sortBy,
            isActive: vendorInputs?.isActive !== "" ? Number(vendorInputs?.isActive) : null,
            // Sort id "7" is the "Outstanding (High -> Low)" option — that only
            // orders by outstandingDebt, so vendors with nothing owed still show
            // up (sorted last). Pair it with the backend's hasOutstandingDebt
            // filter so picking it actually excludes those vendors.
            hasOutstandingDebt: String(vendorInputs?.sortBy) === "7",
            paging: {
                limit: vendorInputs?.limit,
                offset: vendorInputs?.offset
            }
        }
        dispatch(getVendorListAction(payload));
    }

    const createOrUpdateVendorApi = () => {
        let payload = {
            vendorId: createOrUpdateVendorInputs?.vendorId,
            name: createOrUpdateVendorInputs?.name,
            description: createOrUpdateVendorInputs?.description,
            emailId: createOrUpdateVendorInputs?.emailId,
            mobileNumber: createOrUpdateVendorInputs?.mobileNumber,
            websiteUrl: createOrUpdateVendorInputs?.websiteUrl,
            contactName: createOrUpdateVendorInputs?.contactName,
            streetArea: createOrUpdateVendorInputs?.streetArea,
            city: createOrUpdateVendorInputs?.city,
            zipcode: createOrUpdateVendorInputs?.zipcode,
            stateRefId: createOrUpdateVendorInputs?.stateRefId,
            isActive: (key == "2" && createOrUpdateVendorInputs?.isActive == 1) ? 0 : (key == "2" && createOrUpdateVendorInputs?.isActive == 0) ? 1 : createOrUpdateVendorInputs?.isActive,
            isDeleted: createOrUpdateVendorInputs?.isDeleted
        }
        dispatch(createOrUpdateVendorAction(payload))
        setCreateOrUpdateVendorLoad(true)
        setVendorDeleteInactiveOpen(false)
    }

    useEffect(() => {
        if (vendorInputs?.searchText?.length >= 0 && vendorInputs?.sortBy >= 0 && vendorInputs?.offset >= 0) {
            getVendorListApi();
        }
    }, [vendorInputs?.searchText, vendorInputs?.sortBy, vendorInputs?.isActive, vendorInputs?.offset])

    useEffect(() => {
        if (createOrUpdateVendorLoad == true && createOrUpdateVendorOnLoad == false) {
            setCreateOrUpdateVendorLoad(false);
            toast.success(createOrUpdateVendorInputs?.isDeleted == 1 ? "Vendor deleted successfully" : "Vendor status updated successfully");
            getVendorListApi();
        }
    }, [createOrUpdateVendorLoad, createOrUpdateVendorOnLoad])

    useEffect(() => {
        if (!pendingAuditRecordId || !getVendorList?.length) return;
        const match = getVendorList.find((row: any) => row?.vendorId === pendingAuditRecordId);
        if (match) {
            navigate("/vendor/create", { state: { data: match, isEdit: 1 } });
            setPendingAuditRecordId(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingAuditRecordId, getVendorList])

    const vendorListAction = (key: any, item: any) => {
        if (key == "1") {
            navigate("/vendor/create", { state: { data: item, isEdit: 1 } })
        }
        else if (key == "2") {
            setCreateOrUpdateVendorInputs({
                ...createOrUpdateVendorInputs,
                vendorId: item?.vendorId,
                name: item?.vendorName,
                description: item?.description,
                emailId: item?.emailId,
                mobileNumber: item?.mobileNumber,
                websiteUrl: item?.websiteUrl,
                contactName: item?.contactName,
                streetArea: item?.streetArea,
                city: item?.city,
                zipcode: item?.zipcode,
                stateRefId: item?.stateRefId,
                isActive: item?.isActive,
                isDeleted: 0
            })
            setKey(key);
            setVendorDeleteInactiveOpen(true);
        }
        else if (key == "3") {
            setCreateOrUpdateVendorInputs({
                ...createOrUpdateVendorInputs,
                vendorId: item?.vendorId,
                name: item?.vendorName,
                description: item?.description,
                emailId: item?.emailId,
                mobileNumber: item?.mobileNumber,
                websiteUrl: item?.websiteUrl,
                contactName: item?.contactName,
                streetArea: item?.streetArea,
                city: item?.city,
                zipcode: item?.zipcode,
                stateRefId: item?.stateRefId,
                isActive: item?.isActive,
                isDeleted: 1
            })
            setKey(key);
            setVendorDeleteInactiveOpen(true);
        }
    }

    // DataTable gives a 1-based page; reuse the legacy offset math.
    const onPageChange = (nextPage: number) => {
        onChangePagination(nextPage);
    }

    const confirmText = (key == "2" && createOrUpdateVendorInputs?.isActive == 1) ? "Inactive"
        : (key == "2" && createOrUpdateVendorInputs?.isActive == 0) ? "Active"
            : key == "3" ? "Delete" : "";

    return (
        <div className="mz h-full overflow-y-auto px-6 py-4">
            <ApparelMenu selectedKey="6" />
            <VendorTable
                data={getVendorList}
                loading={getVendorListOnLoad}
                onMenuAction={vendorListAction}
                onViewHistory={getVendorPurchaseHistoryApi}
                onViewDebts={(item: any) => navigate(`/vendor-debt/${item?.vendorId}`)}
                searchValue={vendorInputs?.searchText}
                onSearch={getSearchText}
                searchPlaceholder={"Search By Name/Contact Number/Email/ID"}
                sortBy={vendorInputs?.sortBy}
                onSortChange={getOrderData}
                statusFilter={vendorInputs?.isActive}
                onStatusFilterChange={getStatusFilter}
                title="Vendor"
                headerActions={<Button size="sm" onClick={() => navigate("/vendor/create")}><Plus className="mr-1 h-4 w-4" /> Add</Button>}
                pagination={{
                    page: getVendorListPage?.currentPage || 1,
                    total: getVendorListPage?.totalCount || 0,
                    onChange: onPageChange,
                }}
            />
            <ConfirmDialog
                open={vendorDeleteInactiveOpen}
                onOpenChange={setVendorDeleteInactiveOpen}
                title={`Are you sure want to ${confirmText}?`}
                confirmLabel="Ok"
                cancelLabel={AppConstant?.cancel}
                destructive
                onConfirm={createOrUpdateVendorApi}
            />
            <PurchaseHistorySheet
                open={purchaseHistoryOpen}
                onClose={() => setPurchaseHistoryOpen(false)}
                title={vendorHistorySubject?.vendorName}
                resetKey={vendorHistorySubject?.vendorId}
                historyByDate={vendorPurchaseHistory}
                getItemInvoiceId={(item: any) => item?.invoiceId}
                getItemAmount={(item: any) => Number(item?.totalAmount) || 0}
                renderItem={(item1: any, idx: number) => (
                    <div key={idx} className="flex items-start justify-between gap-3 rounded-lg border p-3">
                        <div className="min-w-0 truncate text-sm text-foreground">{item1?.invoiceId}</div>
                        <div className="whitespace-nowrap text-sm tabular-nums">₹{Number(item1?.totalAmount ?? 0).toFixed(2)}</div>
                    </div>
                )}
            />
        </div>
    )
}

export default VendorList;
